import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Card from "@components/cards/Card/Card";
import TextInput from "@components/inputs/TextInput/TextInput";
import SelectInput from "@components/inputs/SelectInput/SelectInput";
import { REGION_OPTIONS } from "@util/domain";
import MediaSelector from "@components/inputs/MediaSelector/MediaSelector";
import styles from "@styles/CommonEditorPageLayout.module.css";
import {
    isRequiredField,
    isValidAlphanumeric,
    isValidHexColor,
    isValidMaxLength,
    isValidUrl,
} from "@util/validators";
import UnsavedChangesPrompt from "@components/misc/UnsavedChangesPrompt";
import EditorCompleteButtons from "@components/buttons/EditorCompleteButtons/EditorCompleteButtons";
import { MAX_BUSINESS_CONTACT_NAME_LENGTH, MAX_BUSINESS_IFRAME_URL_LENGTH, MAX_BUSINESS_LOGO_SIZE, MAX_BUSINESS_NAME_LENGTH, MAX_BUSINESS_SUBDOMAIN_LENGTH } from "@constants/validation";
import { createBusiness, fetchBusiness, updateBusiness } from "@services/businessService";
import { AppTheme, BusinessUpdate } from "@models/Business";
import { MediaFile } from "@models/Common";
import { delay } from "@util/time";
import LoadSaveErrorDisplay from "@components/display/LoadSaveErrorDisplay/LoadSaveErrorDisplay";
import { AxiosProgressEvent } from "axios";

const BusinessEditorPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const mode = searchParams.get("mode");
    const id = searchParams.get("id");

    const [loading, setLoading] = useState(mode === "edit");
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [changedFields, setChangedFields] = useState<BusinessUpdate>({});

    const [name, setName] = useState("");
    const [contactName, setContactName] = useState("");
    const [subdomain, setSubdomain] = useState("");
    const [adminEmail, setAdminEmail] = useState("");
    const [logo, setLogo] = useState<MediaFile | null>(null);
    const [theme, setAppTheme] = useState<AppTheme>({
        buttonColour: "",
        buttonTextColour: "",
    });
    const [iframeUrl, setIframeUrl] = useState("");
    const [firehawkNoticesUrl, setFirehawkNoticesUrl] = useState("");
    const [naocaNoticesUrl, setNaocaNoticesUrl] = useState("");
    const [region, setRegion] = useState<string>("au"); // New businesses default to Australia

    const [nameError, setNameError] = useState("");
    const [contactNameError, setContactNameError] = useState("");
    const [subdomainError, setSubdomainError] = useState("");
    const [logoError, setLogoError] = useState("");
    const [themeError, setThemeError] = useState({
        buttonColourError: "",
        buttonTextColourError: "",
    });
    const [iframeUrlError, setIframeUrlError] = useState("");
    const [firehawkNoticesUrlError, setFirehawkNoticesUrlError] = useState("");
    const [naocaNoticesUrlError, setNaocaNoticesUrlError] = useState("");
    const [submitErrorMessage, setSubmitErrorMessage] = useState("");

    const hasUnsavedChanges = Object.keys(changedFields).length > 0;

    // Load business information in edit mode
    useEffect(() => {
        if (mode === "edit") {
            const fetchData = async () => {
                try {
                    setLoading(true);
                    if (!id) {
                        throw new Error("No ID.");
                    }
                    const data = await fetchBusiness(id);
                    setName(data.name);
                    setContactName(data.contactName || "");
                    setSubdomain(data.subdomain);
                    setAdminEmail(data.adminEmail);
                    setLogo(data.logo);
                    setAppTheme(data.theme);
                    setIframeUrl(data.iframeUrl || "");
                    setFirehawkNoticesUrl(data.firehawkNoticesUrl || "");
                    setNaocaNoticesUrl(data.naocaNoticesUrl || "");
                    setRegion(data.region || "au");
                } catch (err) {
                    setError("Failed to fetch business information.");
                } finally {
                    setLoading(false);
                }
            };
            void fetchData();
        }
    }, [mode, id]);

    // Validation functions
    const validateName = useCallback((val: string) => {
        const result = isRequiredField(val, "Business name");
        if (result) return result;
        return isValidMaxLength(val, MAX_BUSINESS_NAME_LENGTH);
    }, []);

    const validateContactName = useCallback((val: string) => {
        return isValidMaxLength(val, MAX_BUSINESS_CONTACT_NAME_LENGTH);
    }, []);

    const validateSubdomain = useCallback((val: string) => {
        const result = isValidAlphanumeric(val);
        if (result) return result;
        return isValidMaxLength(val, MAX_BUSINESS_SUBDOMAIN_LENGTH);
    }, []);

    const validateUrl = useCallback((val: string) => {
        if (!val) {
            return val;
        }
        const result = isValidUrl(val);
        if (result) return result;
        return isValidMaxLength(val, MAX_BUSINESS_IFRAME_URL_LENGTH);
    }, []);

    const validateThemeColour = useCallback((val: string) => {
        return isValidHexColor(val);
    }, []);

    // Handlers with validation
    const onNameChange = useCallback((val: string) => {
        setName(val);
        setNameError(validateName(val));
        setChangedFields((prev) => ({ ...prev, name: val }));
    }, [validateName]);

    const onContactNameChange = useCallback((val: string) => {
        setContactName(val);
        setContactNameError(validateContactName(val));
        setChangedFields((prev) => ({ ...prev, contactName: val }));
    }, [validateContactName]);

    const onSubdomainChange = useCallback((val: string) => {
        setSubdomain(val);
        setSubdomainError(validateSubdomain(val));
        setChangedFields((prev) => ({ ...prev, subdomain: val }));
    }, [validateSubdomain]);

    const onIframeUrlChange = useCallback((val: string) => {
        setIframeUrl(val);
        setIframeUrlError(validateUrl(val));
        setChangedFields((prev) => ({ ...prev, iframeUrl: val }));
    }, [validateUrl]);

    const onFirehawkNoticesUrlChange = useCallback((val: string) => {
        setFirehawkNoticesUrl(val);
        setFirehawkNoticesUrlError(validateUrl(val));
        setChangedFields((prev) => ({ ...prev, firehawkNoticesUrl: val }));
    }, [validateUrl]);

    const onNaocaNoticesUrlChange = useCallback((val: string) => {
        setNaocaNoticesUrl(val);
        setNaocaNoticesUrlError(validateUrl(val));
        setChangedFields((prev) => ({ ...prev, naocaNoticesUrl: val }));
    }, [validateUrl]);

    const onThemeChange = useCallback((field: keyof AppTheme, value: string) => {
        const updatedTheme = {
            ...theme,
            [field]: value,
        };

        setAppTheme(updatedTheme);

        const validationError = value ? validateThemeColour(value) : "";

        setThemeError((prevErrors) => ({
            ...prevErrors,
            [`${field}Error`]: validationError,
        }));

        setChangedFields((prev) => ({
            ...prev,
            theme: updatedTheme,
        }));
    }, [theme, validateThemeColour]);

    const progressCallback = useCallback((progressEvent: AxiosProgressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
        setUploadProgress(progress);
    }, []);

    // Save handler
    const handleSave = async () => {
        let isValid = true;

        const nameValidationError = validateName(name);
        setNameError(nameValidationError);
        if (nameValidationError) isValid = false;

        const subdomainValidationError = validateSubdomain(subdomain);
        setSubdomainError(subdomainValidationError);
        if (subdomainValidationError) isValid = false;

        const iframeUrlValidationError = validateUrl(iframeUrl);
        setIframeUrlError(iframeUrlValidationError);
        if (iframeUrlValidationError) isValid = false;

        const firehawkNoticesUrlValidationError = validateUrl(firehawkNoticesUrl);
        setFirehawkNoticesUrl(firehawkNoticesUrlValidationError);
        if (firehawkNoticesUrlValidationError) isValid = false;

        const naocaNoticesUrlValidationError = validateUrl(naocaNoticesUrl);
        setNaocaNoticesUrl(naocaNoticesUrlValidationError);
        if (naocaNoticesUrlValidationError) isValid = false;

        if (isValid) {
            try {
                setIsSaving(true);
                setUploadProgress(0);
                setSubmitErrorMessage("");
                let createdId = "";
                console.log("Changed fields to save:", changedFields);

                if (mode === "add") {
                    createdId = await createBusiness(changedFields, progressCallback);
                } else {
                    if (!id) {
                        throw new Error("No ID.");
                    }
                    await updateBusiness(id, changedFields, progressCallback);
                }

                setChangedFields({});
                setIsSaving(false);
                await delay(1); // Wait for saving state to be cleared
                if (mode === "add") {
                    // Change to edit mode after creation
                    searchParams.set("mode", "edit");
                    searchParams.set("id", createdId);
                    navigate(`?${searchParams.toString()}`, { replace: true });
                }
            } catch (err) {
                const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || "Failed to save business information. Please try again.";
                setSubmitErrorMessage(msg);
                alert(msg);
            } finally {
                setIsSaving(false);
            }
        }
    };

    const renderContent = () => {
        if (loading || isSaving || error) {
            return (
                <LoadSaveErrorDisplay
                    isLoading={loading}
                    isSaving={isSaving}
                    progress={changedFields.logo ? uploadProgress : undefined}
                    error={error}
                />
            );
        }

        return (
            <div className={styles.container}>
                <div className={styles.headerContainer}>
                    <h1>
                        {mode === "add" ? "Add" : "Edit"}
                        {" "}
                        Business
                    </h1>
                </div>
                <div className={styles.buttonGroup}>
                    <EditorCompleteButtons
                        hasUnsavedChanges={hasUnsavedChanges}
                        isSaving={isSaving}
                        isNew={mode === "add"}
                        onSave={handleSave}
                    />
                </div>
                <div className={styles.contentContainer}>
                    <h2>General</h2>
                    <Card className={styles.cardContainer}>
                        <TextInput
                            label="Business Name *"
                            id="name"
                            value={name}
                            onChange={onNameChange}
                            placeholder="Enter business name..."
                            errorMessage={nameError}
                        />
                        <TextInput
                            label="Contact Name"
                            id="contactName"
                            value={contactName}
                            onChange={onContactNameChange}
                            placeholder="Enter contact name..."
                            errorMessage={contactNameError}
                        />
                        <SelectInput
                            label="Region *"
                            id="region"
                            value={region}
                            onChange={(val) => {
                                setRegion(val);
                                setChangedFields((prev) => ({ ...prev, region: val === "global" ? "global" : "au" }));
                            }}
                            options={REGION_OPTIONS}
                            placeholder="Select region..."
                        />
                        <TextInput
                            label="Subdomain *"
                            id="subdomain"
                            value={subdomain}
                            onChange={onSubdomainChange}
                            placeholder="Enter subdomain..."
                            disabled={mode === "edit"}
                            errorMessage={subdomainError}
                        />
                        <TextInput
                            label="Admin Email"
                            id="adminEmail"
                            value={adminEmail}
                            onChange={() => {}}
                            placeholder="Enter admin email..."
                            disabled
                        />
                        <MediaSelector
                            label="Logo"
                            id="logo"
                            media={logo}
                            errorMessage={logoError}
                            onMediaChange={(image, err) => {
                                setLogo(image as MediaFile);
                                setLogoError(err);
                                setChangedFields((prev) => ({ ...prev, logo: image as MediaFile }));
                            }}
                            maxSize={MAX_BUSINESS_LOGO_SIZE}
                            mediaTypeFilter="image"
                        />
                        <TextInput
                            label="iFrame Url"
                            id="iframeUrl"
                            value={iframeUrl}
                            onChange={onIframeUrlChange}
                            placeholder="Enter iframe url..."
                            errorMessage={iframeUrlError}
                        />
                        <TextInput
                            label="Firwhawk Notices Url"
                            id="firehawkNoticesUrl"
                            value={firehawkNoticesUrl}
                            onChange={onFirehawkNoticesUrlChange}
                            placeholder="Enter Firehawk notices url..."
                            errorMessage={firehawkNoticesUrlError}
                        />
                        <TextInput
                            label="Naoca Notices Url"
                            id="naocaNoticesUrl"
                            value={naocaNoticesUrl}
                            onChange={onNaocaNoticesUrlChange}
                            placeholder="Enter Naoca notices url..."
                            errorMessage={naocaNoticesUrlError}
                        />
                    </Card>
                    <h2>Theme</h2>
                    <Card className={styles.cardContainer}>
                        <TextInput
                            label="Button Colour"
                            id="buttonColour"
                            value={theme.buttonColour}
                            onChange={(val) => onThemeChange("buttonColour", val)}
                            placeholder="Enter button colour..."
                            errorMessage={themeError.buttonColourError}
                        />
                        <TextInput
                            label="Button Text Colour"
                            id="buttonTextColour"
                            value={theme.buttonTextColour}
                            onChange={(val) => onThemeChange("buttonTextColour", val)}
                            placeholder="Enter button text colour..."
                            errorMessage={themeError.buttonTextColourError}
                        />
                    </Card>
                    {submitErrorMessage && (
                        <p className={styles.errorMessage}>{submitErrorMessage}</p>
                    )}
                </div>
                <UnsavedChangesPrompt hasUnsavedChanges={hasUnsavedChanges} />
            </div>
        );
    };

    return renderContent();
};

export default BusinessEditorPage;
